import * as React from 'react'

import { shortFormatNumber } from '../../../main/util'
import { Boxover } from './Boxover'
import { HoverChart } from './HoverChart'

export type ChartBoxoverEntry = {
  isDelisted?: boolean
  country: string
  company: string
  ticker: string
  industry: string
  marketCap?: number | null
  aum?: number | null
}

interface Props {
  entry: ChartBoxoverEntry
  rowIndex?: number
  children: React.ReactElement
  offsetX?: number
  offsetY?: number
}

const MOUSE_LEAVE_DEBOUNCE_MS = 250

/**
 * Renders basic chart boxover for the stock ticker
 */
export function ChartBoxover({ entry, rowIndex = 0, children, offsetX, offsetY }: Props) {
  const [isMouseOver, setIsMouseOver] = React.useState(false)
  const mouseLeaveTimeoutRef = React.useRef<number | null>(null)
  const clearMouseLeaveTimoutRef = React.useRef(() => {
    if (mouseLeaveTimeoutRef.current) {
      window.clearTimeout(mouseLeaveTimeoutRef.current)
      mouseLeaveTimeoutRef.current = null
    }
  })
  const mouseEventsRef = React.useRef({
    onMouseEnter: () => {
      clearMouseLeaveTimoutRef.current()
      setIsMouseOver(true)
    },
    onMouseLeave: () => {
      mouseLeaveTimeoutRef.current = window.setTimeout(() => {
        setIsMouseOver(false)
      }, MOUSE_LEAVE_DEBOUNCE_MS)
    },
  })

  const value = React.useMemo(() => {
    if (Number.isFinite(entry.aum)) {
      return 'AUM: ' + shortFormatNumber(entry.aum! * 1_000_000)
    }
    return shortFormatNumber(entry.marketCap ? entry.marketCap * 1_000_000 : null)
  }, [entry.marketCap, entry.aum])

  React.useEffect(
    () => () => {
      clearMouseLeaveTimoutRef.current()
    },
    []
  )

  const child = React.cloneElement(children, mouseEventsRef.current)

  if (!isMouseOver || entry.isDelisted) {
    return child
  }

  return (
    <Boxover
      key={`${entry.ticker}-${entry.marketCap}-${rowIndex}`}
      theme="chart"
      body={
        <>
          <HoverChart ticker={entry.ticker} />
          <div>
            <b>{entry.company}</b>
            {entry.industry} <span>•</span> {entry.country} <span>•</span> {value}
          </div>
        </>
      }
      delay={0}
      offsetX={offsetX ?? 50}
      offsetY={offsetY ?? (rowIndex < 10 ? 0 : -260)}
    >
      {child}
    </Boxover>
  )
}
